import { getSupabase } from './supabase';

const ENHANCEMENT_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/legal-search-enhancement`;
const PIPELINE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/legal-search-pipeline`;
const CONTRACT_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/contract-review-pipeline`;
const DOCUMENT_ANALYSIS_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/document-analysis`;
const ADD_CASE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/add-case`;
const DELETE_CASE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-case`;
const GENERATE_MEMO_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-memo`;

function safeJsonParse<T = unknown>(jsonString: string, defaultValue?: T): T | null {
  if (!jsonString || typeof jsonString !== 'string') {
    return defaultValue ?? (null as T);
  }

  try {
    const trimmed = jsonString.trim();
    if (!trimmed) {
      return defaultValue ?? (null as T);
    }
    return JSON.parse(trimmed) as T;
  } catch (error) {
    return defaultValue ?? (null as T);
  }
}

export async function safeResponseJson(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const text = await response.text();
    if (!text || !text.trim()) {
      return null;
    }

    try {
      return JSON.parse(text);
    } catch (parseError) {
      throw new Error(
        `Invalid JSON response from server: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`
      );
    }
  } catch (error) {
    if (error instanceof Error && error.message.includes('Invalid JSON')) {
      throw error;
    }
    throw new Error(`Failed to read response: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

interface EnhancementRequest {
  prompt_id: string;
  templateVariables: Array<{
    key: string;
    value: string | string[];
    type?: string;
  }>;
  model_config?: {
    model: string;
  };
}

interface PipelineRequest {
  research_name: string;
  jurisdiction: string;
  legal_field: string;
  case_brief?: string;
  entity_id: string;
  client_id: string;
  result_group_id?: string;
  question?: string;
  document_ids?: string[];
  metadata?: {
    initial_case_brief?: string;
    initial_question?: string;
    questions_and_answers?: Array<{
      question: string;
      answer: string | 'skipped';
    }>;
  };
}

interface DeleteCaseRequest {
  result_id: string;
  case_name: string;
  question?: string;
  document_ids?: string[];
}

interface APIResponse {
  success: boolean;
  data?: Record<string, unknown>;
  error?: string;
}

async function makeAPICall(url: string, request: Record<string, unknown> | object): Promise<APIResponse> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);

    let jwt: string | undefined;
    try {
      const supabase = await getSupabase();
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        jwt = session?.access_token;
      } else {
        jwt = undefined;
      }
    } catch {
      jwt = undefined;
    }

    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    if (anonKey) {
      headers['apikey'] = anonKey;
    }
    if (jwt) {
      headers['Authorization'] = `Bearer ${jwt}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json')) {
      return {
        success: false,
        error: 'Unexpected response format',
      };
    }

    if (!response.ok) {
      let errorText = '';
      try {
        errorText = await response.text();
      } catch (textError) {
        errorText = 'Unknown server error';
      }
      return {
        success: false,
        error: `API error (${response.status}): ${errorText || 'Unknown server error'}`,
      };
    }

    let data;
    try {
      data = await safeResponseJson(response);
    } catch (jsonError) {
      return {
        success: false,
        error:
          jsonError instanceof Error
            ? jsonError.message
            : 'Invalid JSON response from server - the response may be corrupted or improperly formatted',
      };
    }

    return { success: true, data: data as Record<string, unknown> };
  } catch (err) {
    let message = 'Unknown network error';
    if (err instanceof Error) {
      if (err.name === 'AbortError') {
        message = 'Request timeout - the server took too long to respond (30 seconds)';
      } else if (err.name === 'TypeError' && err.message.includes('fetch')) {
        message = 'Network connection failed - check your internet connection and CORS settings';
      } else if (err.message.includes('CORS')) {
        message = 'CORS error - the server may not allow requests from this domain';
      } else if (err.message.includes('timeout')) {
        message = 'Request timeout - the server took too long to respond';
      } else {
        message = err.message;
      }
    }

    return {
      success: false,
      error: `Network error: ${message}`,
    };
  }
}

export async function getClarificationQuestions(jurisdiction: string, userInput: string): Promise<APIResponse> {
  const request: EnhancementRequest = {
    prompt_id: 'legal_search/clarification_questions.json',
    templateVariables: [
      { key: 'jurisdiction', value: jurisdiction },
      { key: 'userInput', value: userInput },
    ],
  };

  const result = await makeAPICall(ENHANCEMENT_FUNCTION_URL, request);

  if (result.data?.questions && Array.isArray(result.data.questions)) {
    return { success: true, data: { questions: result.data.questions } };
  }

  return result;
}

export async function finalizeCaseBrief(
  jurisdiction: string,
  userInput: string,
  questions: string[],
  answers: Record<number, { answer: boolean | null; details?: string }>,
  question?: string,
  document_ids?: string[]
): Promise<APIResponse> {
  const hasCaseBrief = !!userInput?.trim();
  const hasQuestion = !!question?.trim();

  let caseBriefData: Record<string, unknown> = {};
  let questionData: Record<string, unknown> = {};
  let lastSuccess = false;

  if (hasCaseBrief) {
    const qnaArray = questions
      .map((q, index) => {
        const answer = answers[index + 1];
        if (answer == null || answer.answer === null) return null;
        const details = answer.details;
        return {
          question: q,
          answer: answer.answer ? (details ? `Yes, ${details}` : 'Yes') : details ? `No, ${details}` : 'No',
        };
      })
      .filter((item) => item !== null) as Array<{ question: string; answer: string }>;
    const qnaString = `{ qna: ${JSON.stringify(qnaArray)} }`;

    const request: EnhancementRequest = {
      prompt_id: 'legal_search/finalize_case_brief.json',
      templateVariables: [
        { key: 'jurisdiction', value: jurisdiction },
        { key: 'userInput', value: userInput },
        { key: 'qna', value: qnaString },
      ],
    };

    const result = await makeAPICall(ENHANCEMENT_FUNCTION_URL, request);
    lastSuccess = result.success;
    if (result.success && result.data) {
      const raw = result.data as Record<string, unknown>;
      const unwrapped = raw?.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : raw;
      const output =
        unwrapped?.output && typeof unwrapped.output === 'object'
          ? (unwrapped.output as Record<string, unknown>)
          : unwrapped;
      caseBriefData = (output ?? unwrapped ?? raw) as Record<string, unknown>;
    }
  }

  if (hasQuestion) {
    const questionVal = question ?? '';
    const requestQuestion: EnhancementRequest = {
      prompt_id: 'legal_search/finalize_question.json',
      templateVariables: [
        { key: 'jurisdiction', value: jurisdiction },
        { key: 'question', value: questionVal },
        { key: 'documents', value: document_ids ?? [], type: 'documents' },
      ],
    };

    const resultQuestion = await makeAPICall(ENHANCEMENT_FUNCTION_URL, requestQuestion);
    lastSuccess = resultQuestion.success;
    if (resultQuestion.success && resultQuestion.data) {
      const raw = resultQuestion.data as Record<string, unknown>;
      const unwrapped = raw?.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : raw;
      const output =
        unwrapped?.output && typeof unwrapped.output === 'object'
          ? (unwrapped.output as Record<string, unknown>)
          : unwrapped;
      questionData = (output ?? unwrapped ?? raw) as Record<string, unknown>;
    }
  }

  const mergedData: Record<string, unknown> = {};
  if (hasCaseBrief && Object.keys(caseBriefData).length > 0) {
    mergedData.research_name = caseBriefData.brief_name ?? caseBriefData.research_name ?? caseBriefData.name;
    mergedData.legal_field = caseBriefData.legal_field ?? caseBriefData.practice_area;
    mergedData.case_brief = caseBriefData.case_brief;
  }
  if (hasQuestion && Object.keys(questionData).length > 0) {
    mergedData.question = questionData.question;
    if (!hasCaseBrief) {
      mergedData.research_name = questionData.research_name ?? questionData.brief_name ?? questionData.name;
      mergedData.legal_field = questionData.legal_field ?? questionData.practice_area;
    }
  }

  if (Object.keys(mergedData).length > 0 || lastSuccess) {
    return { success: true, data: mergedData };
  }

  return { success: false, error: 'No finalization data returned' };
}

export async function finalizeQuestion(
  jurisdiction: string,
  question: string,
  document_ids?: string[]
): Promise<APIResponse> {
  const request: EnhancementRequest = {
    prompt_id: 'legal_search/finalize_question.json',
    templateVariables: [
      { key: 'jurisdiction', value: jurisdiction },
      { key: 'question', value: question },
      { key: 'documents', value: document_ids ?? [], type: 'documents' },
    ],
  };

  const result = await makeAPICall(ENHANCEMENT_FUNCTION_URL, request);
  if (!result.success || !result.data) {
    return result;
  }

  const raw = result.data as Record<string, unknown>;
  const unwrapped = raw?.data && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : raw;
  const output =
    unwrapped?.output && typeof unwrapped.output === 'object'
      ? (unwrapped.output as Record<string, unknown>)
      : unwrapped;
  const questionData = (output ?? unwrapped ?? raw) as Record<string, unknown>;

  const questionResult = questionData?.question != null ? String(questionData.question) : question;
  const research_name = questionData?.research_name ?? questionData?.brief_name ?? questionData?.name;
  const legal_field = questionData?.legal_field ?? questionData?.practice_area;

  return {
    success: true,
    data: {
      question: questionResult,
      ...(research_name != null && { research_name: String(research_name) }),
      ...(legal_field != null && { legal_field: String(legal_field) }),
    },
  };
}

export async function submitToLegalSearchPipeline(
  research_name: string,
  jurisdiction: string,
  legal_field: string,
  case_brief: string | undefined,
  entity_id: string,
  client_id: string,
  metadata?: {
    initial_case_brief?: string;
    initial_question?: string;
    questions_and_answers?: Array<{
      question: string;
      answer: string | 'skipped';
    }>;
  },
  result_group_id?: string,
  question?: string,
  document_ids?: string[]
): Promise<APIResponse> {
  const request: PipelineRequest = {
    research_name,
    jurisdiction,
    legal_field,
    entity_id,
    client_id,
    ...(case_brief !== undefined && { case_brief }),
    ...(result_group_id && { result_group_id }),
    ...(metadata && { metadata }),
    ...(question !== undefined && { question }),
    ...(document_ids !== undefined && document_ids.length > 0 && { document_ids }),
  };

  return makeAPICall(PIPELINE_FUNCTION_URL, request);
}

export async function submitToContractReviewPipeline(
  jurisdiction: string,
  document_id: string,
  entity_id: string,
  client_id: string,
  document_name?: string
): Promise<APIResponse> {
  const request: Record<string, string> = {
    jurisdiction,
    document_id,
    entity_id,
    client_id,
  };
  if (document_name) request.document_name = document_name;
  return makeAPICall(CONTRACT_FUNCTION_URL, request);
}

export async function submitDocumentAnalysis(documentId: string): Promise<APIResponse> {
  const request = { document_id: documentId };
  return makeAPICall(DOCUMENT_ANALYSIS_FUNCTION_URL, request);
}

export async function validateCase(caseName: string, jurisdiction: string): Promise<APIResponse> {
  const countryOnly = jurisdiction.split(',')[0].trim();

  const caseDetailsJson = {
    jurisdiction: countryOnly,
    case_name: caseName,
  };

  const jsonString = JSON.stringify(caseDetailsJson);

  const request: EnhancementRequest = {
    prompt_id: 'case_validator',
    templateVariables: [{ key: 'caseDetails', value: jsonString }],
    model_config: {
      model: 'gpt-5-mini',
    },
  };

  const result = await makeAPICall(ENHANCEMENT_FUNCTION_URL, request);

  try {
    const nested = result.data?.data as Record<string, unknown> | undefined;
    const output = nested?.output || result.data?.output;

    if (!output) {
      return {
        success: false,
        error: 'No output found in API response',
      };
    }

    let jsonString: string;

    if (typeof output === 'object' && output !== null) {
      const outputObj = output as Record<string, unknown>;
      if (outputObj.case_name && outputObj.citation) {
        return {
          success: true,
          data: {
            case_name: outputObj.case_name,
            citation: outputObj.citation,
          },
        };
      }
      jsonString = JSON.stringify(output);
    } else if (typeof output === 'string') {
      jsonString = output;
    } else {
      return {
        success: false,
        error: `Unexpected output type: ${typeof output}`,
      };
    }

    jsonString = jsonString.trim();
    if (jsonString.startsWith('```json')) {
      jsonString = jsonString.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (jsonString.startsWith('```')) {
      jsonString = jsonString.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }

    jsonString = jsonString.trim();

    const parsedData = safeJsonParse<Record<string, unknown>>(jsonString);
    if (!parsedData) {
      return {
        success: false,
        error: 'Failed to parse JSON response: Invalid or empty JSON string',
      };
    }

    if (parsedData.case_name && parsedData.citation) {
      return {
        success: true,
        data: {
          case_name: parsedData.case_name,
          citation: parsedData.citation,
        },
      };
    } else {
      return {
        success: false,
        error: 'Invalid response format: missing case_name or citation',
      };
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to parse response: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

export async function enhanceResearch(
  researchId: string,
  validatedCases: Array<{ case_name: string; citation: string }>,
  generalSearchOptions: string[],
  documentIds?: string[]
): Promise<APIResponse> {
  const request: Record<string, unknown> = {
    research_id: researchId,
    validated_cases: validatedCases,
    topics: generalSearchOptions,
  };
  if (documentIds !== undefined) {
    request.document_ids = documentIds;
  }

  return makeAPICall(ADD_CASE_FUNCTION_URL, request);
}

export async function getLegalDomainTopics(caseBrief: string): Promise<APIResponse> {
  const request: EnhancementRequest = {
    prompt_id: 'legal_domain_classifier',
    templateVariables: [{ key: 'caseBrief', value: caseBrief }],
  };

  const result = await makeAPICall(ENHANCEMENT_FUNCTION_URL, request);

  try {
    const nested = result.data?.data as Record<string, unknown> | undefined;
    const output = nested?.output || result.data?.output;

    if (!output) {
      return {
        success: false,
        error: 'No output found in API response',
      };
    }

    let topics: string[] = [];

    if (Array.isArray(output)) {
      topics = output;
    } else if (typeof output === 'string') {
      let jsonString = output.trim();
      if (jsonString.startsWith('```json')) {
        jsonString = jsonString.replace(/^```json\s*/, '').replace(/\s*```$/, '');
      } else if (jsonString.startsWith('```')) {
        jsonString = jsonString.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }

      jsonString = jsonString.trim();
      const parsed = safeJsonParse<Record<string, unknown> | string[]>(jsonString);
      if (!parsed) {
        return {
          success: false,
          error: 'Failed to parse topics JSON: Invalid or empty JSON string',
        };
      }

      if (Array.isArray(parsed)) {
        topics = parsed;
      } else if (parsed.topics && Array.isArray(parsed.topics)) {
        topics = parsed.topics;
      } else if (parsed.array && Array.isArray(parsed.array)) {
        topics = parsed.array;
      }
    } else if (typeof output === 'object' && output !== null) {
      if (Array.isArray(output)) {
        topics = output;
      } else {
        const obj = output as Record<string, unknown>;
        if (obj.topics && Array.isArray(obj.topics)) {
          topics = obj.topics;
        } else if (obj.array && Array.isArray(obj.array)) {
          topics = obj.array;
        }
      }
    }

    if (topics.length > 0) {
      return {
        success: true,
        data: { topics },
      };
    } else {
      return {
        success: false,
        error: 'No topics found in response',
      };
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to parse response: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

export async function deleteCase(
  resultId: string,
  caseName: string,
  options?: { question?: string; document_ids?: string[] }
): Promise<APIResponse> {
  const request: DeleteCaseRequest = {
    result_id: resultId,
    case_name: caseName,
    ...(options?.question && { question: options.question }),
    ...(options?.document_ids && options.document_ids.length > 0 && { document_ids: options.document_ids }),
  };

  return makeAPICall(DELETE_CASE_FUNCTION_URL, request);
}

export async function updateCaseBrief(caseBrief: string, updateInstructions: string): Promise<APIResponse> {
  const request: EnhancementRequest = {
    prompt_id: 'legal_search/update_case_brief/v1.json',
    templateVariables: [
      { key: 'caseBrief', value: caseBrief },
      { key: 'updateInstructions', value: updateInstructions },
    ],
  };

  const result = await makeAPICall(ENHANCEMENT_FUNCTION_URL, request);

  try {
    const nested = result.data?.data as Record<string, unknown> | undefined;
    const output = nested?.output || result.data?.output;

    if (!output) {
      return {
        success: false,
        error: 'No output found in API response',
      };
    }

    let updatedCaseBrief = '';

    if (typeof output === 'string') {
      updatedCaseBrief = output.trim();
    } else if (typeof output === 'object' && output !== null) {
      const outputObj = output as Record<string, unknown>;
      if (outputObj.case_brief && typeof outputObj.case_brief === 'string') {
        updatedCaseBrief = outputObj.case_brief;
      } else if (outputObj.updated_case_brief && typeof outputObj.updated_case_brief === 'string') {
        updatedCaseBrief = outputObj.updated_case_brief;
      } else {
        updatedCaseBrief = JSON.stringify(output);
      }
    }

    if (updatedCaseBrief.length > 0) {
      return {
        success: true,
        data: { updated_case_brief: updatedCaseBrief },
      };
    } else {
      return {
        success: false,
        error: 'No updated case brief found in response',
      };
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to parse response: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

export async function sendNotificationEmail(
  type: 'research_complete' | 'contract_review_complete',
  name: string
): Promise<void> {
  try {
    const supabase = await getSupabase();
    await supabase.functions.invoke('send-notification-email', {
      body: { type, name },
    });
  } catch {
    // fire and forget
  }
}

export async function generateMemo(
  resultId: string,
  researchData: object,
  inputs: { jurisdiction: string; question: string; facts: string }
): Promise<APIResponse> {
  const request = {
    result_id: resultId,
    memoInfo: researchData,
    jurisdiction: inputs.jurisdiction,
    question: inputs.question,
    facts: inputs.facts,
  };

  return makeAPICall(GENERATE_MEMO_FUNCTION_URL, request);
}
